import { ActionTree, GetterTree, MutationTree, Module } from 'vuex';
import { setupApollo } from '../../../lib/apollo_setup';
import { FETCH_ACCOUNT_DATA } from '@/graphql/account/queries';
import { Account, AccountData } from '../../../models/account';
import { currentJwt, rolesArray } from '@/lib/jwt';

export interface AccountState {
  accountData: AccountData | null;
  error: string | null;
}

interface AccountLocation {
  cityname: string;
  stateabbr: string;
  latitude: number;
  longitude: number;
  x: number;
  y: number;
}

const apolloProvider = setupApollo();

// Deduplication: track in-flight fetch requests to prevent duplicate API calls
let fetchAccountDataPromise: Promise<void> | null = null;

const state: AccountState = {
  accountData: null,
  error: null,
};

const getters: GetterTree<AccountState, any> = {
  isAuthenticated: (state): boolean => !!state.accountData,
  getAccount: (state): Account | null => {
    if (!state.accountData) return null;
    return new Account(state.accountData);
  },
  getZipCode: (_, getters): string | null => getters.getAccount?.location?.zipcode || null,
  getLocation: (_, getters): AccountLocation | null => {
    const location = getters.getAccount?.location;
    if (location) {
      return {
        cityname: location.cityname,
        stateabbr: location.stateabbr,
        latitude: location.latitude,
        longitude: location.longitude,
        y: location.latitude,
        x: location.longitude,
      };
    }
    return null;
  },
  accountType: (_, getters): 'parent' | 'sitter' => {
    const account = getters.getAccount;
    return account?.isSitter?.() ? 'sitter' : 'parent';
  },
};

const mutations: MutationTree<AccountState> = {
  setAccountData(state, accountData: AccountData) {
    state.accountData = accountData;
  },
  setError(state, error: string) {
    state.error = error;
  },
};

const actions: ActionTree<AccountState, any> = {
  async fetchAccountData({ commit }): Promise<void> {
    if (!currentJwt()) return Promise.resolve();

    // Return existing promise if fetch is already in progress (deduplication)
    if (fetchAccountDataPromise) {
      return fetchAccountDataPromise;
    }

    fetchAccountDataPromise = (async (): Promise<void> => {
      try {
        const result = await apolloProvider.defaultClient.query({
          query: FETCH_ACCOUNT_DATA,
          fetchPolicy: 'network-only',
        });

        if (result.errors?.length) {
          throw new Error(result.errors.map((e) => e.message).join(', '));
        }

        const { viewer } = result.data;
        // ensure accountData is not null or undefined
        if (!viewer) return;

        const accountData = {
          ssoUUID: viewer.id,
          id: viewer.id,
          name: viewer.name,
          firstName: viewer.firstName,
          lastName: viewer.lastName,
          email: viewer.email,
          originalPhoto: viewer.photoUrl,
          croppedPhoto: viewer.photoUrl,
          location: {
            latitude: parseFloat(viewer.location.geoPoint.y),
            longitude: parseFloat(viewer.location.geoPoint.x),
            zipcode: viewer.location.zipcode,
            cityname: viewer.location.cityname,
            stateabbr: viewer.location.stateabbr,
          },
          hasPremiumHistory: viewer.hasPremiumHistory,
          profilePhotoStatus: viewer.profilePhotoStatus,
          createdAt: viewer.createdAt,
          roles: rolesArray(),
        } as AccountData;

        const account = new Account(accountData);
        if (account.isParent()) {
          accountData.careType = viewer.careType;
        } else if (account.isSitter()) {
          accountData.careType = viewer.providerType;
        }

        commit('setAccountData', accountData);
      } catch (error: any) {
        console.error('Error fetching account data:', error);

        const errorMessage = error?.message || 'Failed to fetch account data.';
        commit('setError', errorMessage);

        throw error;
      } finally {
        // Clear the promise after fetch completes (success or failure)
        fetchAccountDataPromise = null;
      }
    })();

    return fetchAccountDataPromise;
  },
};

const accountModule: Module<AccountState, any> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default accountModule;
